import { useEffect, useCallback, useRef } from 'react'
import { useTuningStore } from '../store/tuningStore'
import type { MidiDevice } from '../types'

export const useMidi = () => {
  const {
    setMidiSupported,
    setAvailableDevices,
    setConnectedDevice,
    addActiveNote,
    removeActiveNote
  } = useTuningStore()

  const midiAccessRef = useRef<MIDIAccess | null>(null)
  const inputRef = useRef<MIDIInput | null>(null)

  const checkSupport = useCallback((): boolean => {
    const supported = 'requestMIDIAccess' in navigator
    setMidiSupported(supported)
    return supported
  }, [setMidiSupported])

  const getDevices = useCallback(async (): Promise<MidiDevice[]> => {
    if (!checkSupport()) {
      console.warn('⚠️ Web MIDI API not supported in this browser')
      return []
    }

    try {
      const access = await navigator.requestMIDIAccess({ sysex: false })
      midiAccessRef.current = access

      const devices: MidiDevice[] = []
      access.inputs.forEach((input) => {
        devices.push({
          id: input.id,
          name: input.name || 'Unknown Device',
          manufacturer: input.manufacturer || undefined
        })
      })

      if (devices.length === 0) {
        console.warn('⚠️ No MIDI input devices found. Make sure your keyboard is connected and turned on.')
      }

      setAvailableDevices(devices)

      // Listen for device changes
      access.onstatechange = () => {
        const updatedDevices: MidiDevice[] = []
        access.inputs.forEach((input) => {
          updatedDevices.push({
            id: input.id,
            name: input.name || 'Unknown Device',
            manufacturer: input.manufacturer || undefined
          })
        })
        setAvailableDevices(updatedDevices)
      }

      return devices
    } catch (error) {
      console.error('Error accessing MIDI:', error)
      return []
    }
  }, [checkSupport, setAvailableDevices])

  const connect = useCallback(
    async (deviceId: string) => {
      
      if (!midiAccessRef.current) {
        console.log('📡 Requesting MIDI access...')
        await getDevices()
      }

      if (!midiAccessRef.current) {
        console.error('❌ MIDI access not available')
        throw new Error('MIDI access not available')
      }

      
      const input = midiAccessRef.current.inputs.get(deviceId)
      if (!input) {
        console.error(`❌ Device not found: ${deviceId}`)
        throw new Error(`Device not found: ${deviceId}`)
      }


      // Disconnect previous input
      if (inputRef.current) {
        inputRef.current.onmidimessage = null
      }

      inputRef.current = input
      setConnectedDevice({
        id: input.id,
        name: input.name || 'Unknown Device',
        manufacturer: input.manufacturer || undefined
      })

      // Set up message handler
      input.onmidimessage = (event: MIDIMessageEvent) => {
        if (!event.data) {
          console.warn('⚠️ MIDI event has no data')
          return
        }
        
        if (event.data.length < 3) {
          console.warn(`⚠️ MIDI message too short: length=${event.data.length}`, event.data)
          return
        }
        
        const status = event.data[0]
        const note = event.data[1]
        const velocity = event.data[2]
        const messageType = status & 0xf0


        if (messageType === 0x90 && velocity > 0) {
          // Note On
          addActiveNote(note)
        } else if (messageType === 0x80 || (messageType === 0x90 && velocity === 0)) {
          // Note Off
          console.log(`❌ Note Off: MIDI ${note}`)
          removeActiveNote(note)
        } else {
        }
      }

    },
    [getDevices, setConnectedDevice, addActiveNote, removeActiveNote]
  )

  const disconnect = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.onmidimessage = null
      inputRef.current = null
    }
    setConnectedDevice(null)
  }, [setConnectedDevice])

  useEffect(() => {
    checkSupport()
    if (checkSupport()) {
      getDevices()
    }

    return () => {
      disconnect()
    }
  }, [checkSupport, getDevices, disconnect])

  return {
    checkSupport,
    getDevices,
    connect,
    disconnect
  }
}

